import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { handlePlanLimitError } from '../lib/planLimit';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Chatbot = ({ mode = 'floating' }) => {
  const isPanel = mode === 'panel';
  const [isOpen, setIsOpen] = useState(isPanel);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Salut ! Je suis Skipya, ton assistant admin. Comment puis-je t'aider aujourd'hui ? 🤖" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      const isPlanLimit = handlePlanLimitError(error, navigate);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: isPlanLimit
          ? "Tu as atteint ta limite de messages quotidiens. Passe à un plan supérieur pour continuer à discuter avec moi !"
          : "Oups, j'ai un petit souci. Réessaie dans quelques instants ! 🔧"
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat button */}
      {!isPanel && (
        <motion.button
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#7EC8C8] to-[#4ECDC4] shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow ${isOpen ? 'hidden' : ''}`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="w-6 h-6 text-[#1A1A2E]" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF6B6B] rounded-full flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">1</span>
          </span>
        </motion.button>
      )}

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`${isPanel ? 'relative w-full h-[620px]' : 'fixed bottom-6 right-6 z-50 w-[360px] h-[500px]'} bg-[#1A1A2E] rounded-2xl shadow-2xl border border-[#7EC8C8]/20 flex flex-col overflow-hidden`}
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#7EC8C8] to-[#4ECDC4] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1A1A2E] flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[#7EC8C8]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A2E]" style={{ fontFamily: 'Outfit' }}>Skipya</h3>
                  <p className="text-xs text-[#1A1A2E]/70">Ton assistant admin</p>
                </div>
              </div>
              {!isPanel && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-[#1A1A2E]/10 rounded-full transition-colors"
                  data-testid="chatbot-close"
                >
                  <X className="w-5 h-5 text-[#1A1A2E]" />
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-[#7EC8C8] text-[#1A1A2E] rounded-br-sm'
                        : 'bg-[#F8F9FA]/10 text-[#F8F9FA] rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-[#F8F9FA]/10 p-3 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#7EC8C8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#7EC8C8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#7EC8C8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-[#7EC8C8]/10">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Pose ta question..."
                  disabled={loading}
                  data-testid="chatbot-input"
                  className="flex-1 bg-[#F8F9FA]/5 border-[#7EC8C8]/20 text-[#F8F9FA] placeholder:text-[#94A3B8]/50 focus:border-[#7EC8C8]"
                />
                <Button
                  type="submit"
                  disabled={loading || !input.trim()}
                  data-testid="chatbot-send"
                  className="bg-[#7EC8C8] hover:bg-[#4ECDC4] text-[#1A1A2E] px-4"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
